<template>
    <b-container fluid>
        <b-row class="">
            <b-col cols="12">
                <admin-page-header title="유저 관리" description="Assessment 서비스를 사용하는 유저를 관리합니다."/>
            </b-col>
        </b-row>

        <b-row class="mb-3">
            <b-col cols="12">
                <b-card no-body bg-variant="white">
                    <template #header>
                        <div class="d-flex align-items-center justify-content-between">
                            <h5 class="mb-0">유저 검색하기</h5>

                            <div v-show="is_loading_searching_user">
                                <div class="d-flex align-items-center">
                                    <b-spinner class="mr-2" variant="primary" style="height: 1rem; width: 1rem;"/>
                                    <p class="d-inline-block mb-0">조회중...</p>
                                </div>
                            </div>
                            <div v-show="!is_loading_searching_user">
                                <b-btn
                                    variant="primary"
                                    :disabled="!is_able_to_search_user || is_loading_searching_user"
                                    @click="onSearch"
                                >
                                    검색하기
                                </b-btn>
                            </div>
                        </div>
                    </template>

                    <div class="p-3 border-bottom border-color-gray-400">
                        <b-row>
                            <b-col cols="12" md="6" xl="3" class="mb-3 mb-xl-0">
                                <b-form-group
                                    id="fieldset-input-name"
                                    label="사용자 이름"
                                    label-for="user-input-name"
                                    class="mb-0"
                                >
                                    <b-form-input id="user-input-name"
                                                  v-model="searchFormValue.name"
                                                  :disabled="is_loading_searching_user"
                                                  trim
                                                  @keypress.enter="onSearch"
                                    />
                                </b-form-group>
                            </b-col>
                            <b-col cols="12" md="6" xl="3" class="mb-3 mb-xl-0">
                                <b-form-group
                                    id="fieldset-input-email"
                                    label="로그인 아이디 (E-mail)"
                                    label-for="user-input-email"
                                    class="mb-0"
                                >
                                    <b-form-input id="user-input-email"
                                                  v-model="searchFormValue.email"
                                                  :disabled="is_loading_searching_user"
                                                  trim
                                                  @keypress.enter="onSearch"
                                    />
                                </b-form-group>
                            </b-col>
                            <b-col cols="12" md="6" xl="3" class="mb-3 mb-md-0">
                                <b-form-group
                                    id="fieldset-input-company"
                                    label="소속 기업"
                                    label-for="user-input-company"
                                    class="mb-0"
                                >
                                    <b-form-input id="user-input-company"
                                                  v-model="searchFormValue.company"
                                                  :disabled="is_loading_searching_user"
                                                  trim
                                                  @keypress.enter="onSearch"
                                    />
                                </b-form-group>
                            </b-col>
                            <b-col cols="12" md="6" xl="3">
                                <b-form-group
                                    id="fieldset-input-level"
                                    label="사용자 레벨"
                                    label-for="user-input-level"
                                    class="mb-0"
                                >
                                    <b-form-select id="user-input-level"
                                                   v-model="searchFormValue.level"
                                                   :disabled="is_loading_searching_user"
                                    >
                                        <option :value="null">전체</option>
                                        <option value="2">lv 2 (외부 평가자)</option>
                                        <option value="3">lv 3 (캠페인 평가자)</option>
                                        <option value="4">lv 4 (최고 관리자)</option>
                                    </b-form-select>
                                </b-form-group>
                            </b-col>
                        </b-row>
                    </div>
                    <div class="p-3">
                        <b-row class="">
                            <b-col cols="12">
                                <b-card
                                    :class="[{ 'shadow-sm': searchResults || (!is_loading_searching_user && searchResults) }]"
                                    :bg-variant="!searchResults ? 'light' : 'white'"
                                    no-body
                                >
                                    <template #header>
                                        <h6 class="mb-0">유저 검색 결과</h6>
                                    </template>

                                    <b-table
                                        ref="user-table"
                                        :items="searchResults"
                                        :fields="user_record"
                                        class="mb-0"
                                        table-class="ds-table ds-table-fixed admin-user-table"
                                        empty-html="<h6 class='mt-3 gray-400'>조회된 유저가 없습니다.</h6>"
                                        show-empty
                                        sticky-header="calc(100vh - 360px)"
                                        :busy="is_loading_searching_user"
                                    >
                                        <template #table-busy>
                                            <div class="d-flex align-items-center justify-content-center my-3">
                                                <b-spinner class="mr-2" variant="primary"/>
                                                <span>조회 중...</span>
                                            </div>
                                        </template>
                                        <!-- DS 표 규칙: 행 44 고정 — 칸마다 한 줄, 넘치면 줄이고 전체는 title(툴팁).
                                             막힌 체크박스는 hover 를 못 받으므로 이유는 감싼 span 의 title 로 -->
                                        <template #cell(select)="row">
                                            <span :title="row.item.is_admin > 3 ? '최고 관리자는 권한 부여 대상이 아님' : null">
                                                <input v-model="row.item.select"
                                                       type="checkbox"
                                                       :aria-label="`${row.item.user_name} 선택`"
                                                       :disabled="row.item.is_admin > 3"
                                                       @change="handleRowSelection(row.item)"
                                                >
                                            </span>
                                        </template>

                                        <!-- 번호는 표시 순서 — item.index(목록 위치, 권한 해제 후 갱신에 씀)와 다르다 -->
                                        <template #cell(index)="row">
                                            {{ row.index + 1 }}
                                        </template>

                                        <template #cell(e_mail)="row">
                                            <span class="ds-ellipsis" :title="row.value">{{ row.value }}</span>
                                        </template>

                                        <template #cell(user_name)="row">
                                            <span class="ds-ellipsis" :title="`${row.value || '-'} (ID ${row.item.id})`">
                                                {{ row.value || '-' }} <span class="ds-sub">{{ row.item.id }}</span>
                                            </span>
                                        </template>

                                        <template #cell(company_name)="row">
                                            <span class="ds-ellipsis" :title="`${row.value || '-'} (${row.item.company_id || '-'})`">
                                                <router-link v-if="row.item.company_id" :to="`/admin/company/${row.item.company_id}`" class="ds-detail-link">{{ row.value || '-' }}</router-link><template v-else>{{ row.value || '-' }}</template> <span class="ds-sub">{{ row.item.company_id }}</span>
                                            </span>
                                        </template>

                                        <template #cell(is_admin)="row">
                                            <span class="ds-tag">{{ getUserLevelLabel(row.value) }}</span>
                                        </template>

                                        <!-- 첫 코드 외 N 한 줄, 전체 목록은 title · 펼침 행 -->
                                        <template #cell(project_codes)="row">
                                            <span v-if="row.value && row.value.length" class="ds-ellipsis" :title="row.value.join(', ')">
                                                {{ row.value[0] }} <span v-if="row.value.length > 1" class="ds-sub">외 {{ row.value.length - 1 }}</span>
                                            </span>
                                            <span v-else class="ds-sub">-</span>
                                        </template>

                                        <!-- 행 액션(아이콘 28): 연결 프로젝트 펼치기(코드가 있을 때만 · 빈 자리 유지) · 수정.
                                             펼친 채 코드가 다 해제돼도 접을 수 있게 펼침 중엔 남긴다 -->
                                        <template #cell(actions)="row">
                                            <div class="d-flex justify-content-end" style="gap: 4px">
                                                <button v-if="(row.item.project_codes && row.item.project_codes.length) || row.detailsShowing"
                                                        type="button" class="ds-row-icon-btn"
                                                        :aria-expanded="row.detailsShowing ? 'true' : 'false'"
                                                        :aria-label="`${row.item.user_name} 연결 프로젝트 ${row.detailsShowing ? '접기' : '펼치기'}`"
                                                        :title="`연결 프로젝트 ${row.detailsShowing ? '접기' : '펼치기'}`"
                                                        @click="row.toggleDetails()">
                                                    <AppIcon :name="row.detailsShowing ? 'chevron-up' : 'chevron-down'"/>
                                                </button>
                                                <span v-else class="ds-row-icon-btn" aria-hidden="true"></span>
                                                <button type="button" class="ds-row-icon-btn"
                                                        :aria-label="`${row.item.user_name} 수정`" :title="`${row.item.user_name} 수정`"
                                                        @click="editSearchedUser(row.item)">
                                                    <AppIcon name="pencil"/>
                                                </button>
                                            </div>
                                        </template>
                                        <template #row-details="row">
                                            <div class="d-flex border rounded bg-white mb-3">
                                                <div class="flex-grow-1 p-3">
                                                    <template v-if="row.item.project_codes">
                                                        <div v-for="project_code in row.item.project_codes"
                                                             :key="`user-${row.item.id}-projectcode-${project_code}`"
                                                             class="d-inline-flex align-items-center bg-success text-light rounded-pill px-3 pt-1 pb-1 mr-2"
                                                        >
                                                            {{ project_code }}

                                                            <b-btn variant="link" class="text-light p-0 ml-2 mb-1"
                                                                   :title="`${project_code} 접근 권한 해제`"
                                                                   :aria-label="`${project_code} 접근 권한 해제`"
                                                                   @click="deleteAditorProjectAccess(row.item, project_code)"
                                                            >
                                                                <BIconXCircleFill font-scale="1.3"/>
                                                            </b-btn>
                                                        </div>
                                                    </template>
                                                    <template v-else>
                                                        연결된 프로젝트 없음
                                                    </template>
                                                </div>
                                            </div>
                                        </template>
                                    </b-table>
                                </b-card>
                            </b-col>
                        </b-row>
                    </div>
                </b-card>
            </b-col>
        </b-row>

        <b-row>
            <b-col cols="12">
                <b-card no-body class="shadow-sm" header-bg-variant="white">
                    <template #header>
                        <div class="d-flex align-items-top justify-content-between">
                            <div>
                                <h5 class="mb-1">프로젝트 접근 권한 부여</h5>
                                <p class="mb-0 ml-1">lv.2 또는 lv.3 사용자만 접근 권한 부여 가능합니다.</p>
                            </div>
                            
                            <div>
                                <b-button variant="success" size="md" style="width: 150px"
                                          :disabled="is_giving_project || !selectedUserNum || !project_code"
                                          @click="giveProjectToAuditor">
                                    <b-spinner v-if="is_giving_project" small class="mr-1"/>
                                    권한 부여하기
                                </b-button>
                            </div>
                        </div>
                    </template>

                    <div class="p-3">
                        <b-row class="ml-2">
                            <b-col sm=3>
                                <label>유저 ID:</label>

                            </b-col>
                            <b-col sm=6>
                                총 {{ selectedUserNum }} 명 선택됨
                                <b-button v-b-toggle.collapse-2 size="sm" pill class="ml-3 m-1 mr-2"
                                          variant="outline-success">유저 확인
                                </b-button>
                                <b-button size="sm" pill class="m-1" variant="outline-warning"
                                          @click="resetSelectedUser">유저 초기화
                                </b-button>
                                <b-collapse id="collapse-2" class="mb-3">
                                    <b-card>{{ selectedUserName }}</b-card>
                                </b-collapse>
                            </b-col>
                        </b-row>
                        <b-row class="ml-2 mt-2">
                            <b-col sm=3>
                                <label>프로젝트코드:</label>
                            </b-col>
                            <b-col sm=6>
                                <b-form-input v-model="project_code" class="mb-2" :trim="true"
                                              placeholder="권한을 부여할 프로젝트코드를 입력해주세요."></b-form-input>
                            </b-col>
                        </b-row>
                    </div>
                </b-card>
            </b-col>
        </b-row>

        <b-modal id="user-edit-modal"
                 title="수정하기"
                 hide-footer
                 :no-close-on-esc="is_saving_editing_user"
                 :no-close-on-backdrop="is_saving_editing_user"
                 :hide-header-close="is_saving_editing_user"
                 @hide="onEditModalHide"
                 @hidden="resetEditForm"
        >

            <div class="p-3">
                <b-form @submit="saveEditedUser" @reset="resetUserEditUserForm">
                    <b-form-row>
                        <b-col cols="12">
                            <b-form-group
                                label="사용자 이름"
                                label-for="edit-form-username"
                            >
                                <b-form-input id="edit-form-username"
                                              v-model="editUserFormValue.user_name"
                                              :disabled="is_saving_editing_user"
                                />
                            </b-form-group>
                        </b-col>
                    </b-form-row>

                    <b-form-row>
                        <b-col cols="12">
                            <b-form-group
                                label="이메일(아이디)"
                                label-for="edit-form-email"
                            >
                                <b-form-input id="edit-form-email"
                                              v-model="editUserFormValue.e_mail"
                                              :disabled="is_saving_editing_user"
                                />
                            </b-form-group>
                        </b-col>
                    </b-form-row>

                    <b-form-row>
                        <b-col cols="12">
                            <b-form-group
                                label="기업 ID"
                                label-for="edit-form-companyid"
                            >
                                <b-form-input id="edit-form-companyid"
                                              v-model="editUserFormValue.company_id"
                                              :disabled="is_saving_editing_user"
                                />
                            </b-form-group>
                        </b-col>
                    </b-form-row>

                    <b-form-row>
                        <b-col cols="12">
                            <b-form-group
                                label="사용자 레벨"
                                label-for="edit-form-admin"
                            >
                                <b-form-select id="edit-form-admin"
                                               v-model="editUserFormValue.is_admin"
                                               :disabled="is_saving_editing_user"
                                >
                                    <option value="1">lv 1 (일반 사용자)</option> 
                                    <option value="2">lv 2 (외부 평가자)</option>
                                    <option value="3">lv 3 (캠페인 평가자)</option>
                                    <option value="4">lv 4 (최고 관리자)</option>
                                </b-form-select>
                            </b-form-group>
                        </b-col>
                    </b-form-row>

                    <b-form-row>
                        <b-col cols="12">
                            <hr class="pb-0 my-3">
                        </b-col>
                    </b-form-row>

                    <b-form-row>
                        <b-col cols="12">
                            <div class="text-center">
                                <b-btn variant="primary"
                                       class="mr-3"
                                       :disabled="is_saving_editing_user"
                                       @click="saveEditedUser"
                                >
                                    사용자 수정하기
                                </b-btn>
                                <b-btn variant="secondary"
                                       :disabled="is_saving_editing_user"
                                       @click="closeEditModal"
                                >
                                    취소
                                </b-btn>
                            </div>
                        </b-col>
                    </b-form-row>
                </b-form>
            </div>
        </b-modal>
    </b-container>
</template>

<script>
import {BIconXCircleFill} from 'bootstrap-vue';
import { getErrorMessage } from '@core/utils/api/errorMessage';
import { getUserLevelLabel } from '@shared/components/admin/pages/Management/Company/data';

// 검색 폼 필드 -> URL query 키 (API 파라미터명과 같게, 레벨은 level). 빈 값은 키 생략
const SEARCH_QUERY_KEYS = {name: 'user_name', email: 'e_mail', company: 'company_name', level: 'level'};
const LEVEL_VALUES = ['2', '3', '4'];

export default {
    name: 'AdminUser',
    components: {
        BIconXCircleFill
    },
    data() {
        return {
            searchResults: null,
            is_loading_searching_user: false,
            searchSeq: 0,
            searchFormValue: {
                name: null,
                email: null,
                company: null,
                level: null
            },
            is_saving_editing_user: false,
            // 취소·저장 완료로 닫는 중이면 미저장 확인을 건너뛴다
            editConfirmedClose: false,
            editOriginal: null,
            editUserFormValue: {
                index: null,
                name: null,
                email: null,
                company: null,
                level: null
            },
            project_code: null,
            is_giving_project: false,
            selectedUserIds: {},
            selectedUserNum: 0,

            // DS 표 규칙(행 44 고정 · 한 줄 칸 · 번호는 정렬 없음 · 행 액션은 맨 끝 한 칸).
            // 폭은 머리가 정한다(ds-table-fixed) — 폭을 안 적은 이메일 · 사용자 · 소속 기업 · 연결 프로젝트가 남는 폭을 나눠 갖는다.
            // 유저 ID 는 사용자 칸에, 기업 ID 는 소속 기업 칸에 함께 보인다.
            user_record: [
                {key: 'select', label: '선택', thClass: 'text-center', tdClass: 'text-center', thStyle: {width: '48px'}},
                {key: 'index', label: 'No.', thClass: 'ds-cell-no', tdClass: 'ds-cell-no', thStyle: {width: '56px'}},
                {key: 'e_mail', label: '이메일(로그인 ID)', sortable: true},
                {key: 'user_name', label: '사용자', sortable: true},
                {key: 'company_name', label: '소속 기업', sortable: true},
                {key: 'is_admin', label: '레벨', sortable: true, tdClass: 'ds-cell-nowrap', thStyle: {width: '150px'}},
                {key: 'project_codes', label: '연결 프로젝트'},
                {key: 'actions', label: '', thStyle: {width: '92px'}},
            ]

        };
    },
    computed: {
        is_able_to_search_user() {
            return Object.values(this.searchFormValue).some(_value => !!_value);
        },
        hasCheckedFilters() {
            return this.filters.some(filter => filter.isChecked && filter.value !== '');
        },
        mapSearchUser() {
            const obj = {};
            this.searchResults.forEach((user) => {
                obj[user.id] = user;
            });
            return obj;
        },
        selectedUserName() {
            return Object.values(this.selectedUserIds).reduce((acc, cur) => {
                acc.push(cur.user_name);
                return acc;
            }, []);
        }
    },
    watch: {
        '$route.query'() {
            this.syncSearchFromRoute();
        },
    },
    created() {
        this.syncSearchFromRoute();
    },
    methods: {
        getUserLevelLabel,
        // URL 이 검색 조건의 기준. 새로고침·뒤로가기·다른 메뉴 다녀오기에도 같은 결과를 복원한다
        syncSearchFromRoute() {
            const q = this.$route.query;
            Object.entries(SEARCH_QUERY_KEYS).forEach(([field, key]) => {
                const value = typeof q[key] === 'string' ? q[key].trim() : '';
                this.searchFormValue[field] = value && (field !== 'level' || LEVEL_VALUES.includes(value)) ? value : null;
            });
            if (this.is_able_to_search_user) {
                this.searchUsers();
            } else {
                // 진행 중이던 검색 응답도 버린다
                this.searchSeq += 1;
                this.is_loading_searching_user = false;
                this.searchResults = null;
                this.selectedUserIds = {};
                this.selectedUserNum = 0;
            }
        },
        // 검색 버튼·Enter: 조건을 URL 에 올리고 watcher 가 조회한다. 같은 조건이면 URL 이 안 바뀌므로 직접 조회
        onSearch() {
            if (this.is_loading_searching_user) return;
            if (!this.is_able_to_search_user) {
                // 조건 없이 조회하면 서버가 전체 사용자를 돌려준다
                this.$EventBus.$emit('toast-failure', this.$t('유저 검색 오류'), '검색 조건을 하나 이상 입력해주세요.');
                return;
            }
            const query = {};
            Object.entries(SEARCH_QUERY_KEYS).forEach(([field, key]) => {
                const value = this.searchFormValue[field] == null ? '' : String(this.searchFormValue[field]).trim();
                if (value) query[key] = value;
            });
            const current = this.$route.query;
            const keys = Object.keys(query);
            if (keys.length === Object.keys(current).length && keys.every(key => query[key] === current[key])) {
                this.searchUsers();
                return;
            }
            this.$router.push({query}).catch(() => {});
        },
        async searchUsers() {
            // 뒤로가기 등으로 조건이 연달아 바뀌면 늦게 온 이전 검색 응답을 버린다
            const request = ++this.searchSeq;
            try {
                this.is_loading_searching_user = true;
                this.searchResults = null;
                // 이전 검색의 선택이 남으면 새 결과와 어긋나므로 검색마다 비운다
                this.selectedUserIds = {};
                this.selectedUserNum = 0;
                const {name, email, company, level} = this.searchFormValue;
                const _res_searched_user = await this.$axios.get('/users/admin', {
                    params: {
                        user_name: name, e_mail: email, company_name: company, is_admin: level,
                    }
                });
                if (request !== this.searchSeq) return;

                if (_res_searched_user.data == null) {
                    this.$EventBus.$emit('toast-success', this.$t('검색 완료'), '유저 검색 결과가 없습니다.');
                    return;
                }

                this.searchResults = _res_searched_user.data.map(_user => ({
                    ..._user,
                    project_codes: !_user.project_codes ? _user.project_codes : _user.project_codes.split(',')
                }));
            } catch (error) {
                if (request !== this.searchSeq || error?.response?.status === 401) return;
                this.$EventBus.$emit('toast-failure', this.$t('유저 검색 오류'), '유저 검색 도중 오류가 발생했습니다. 잠시후 재시도 해주세요.');
            } finally {
                if (request === this.searchSeq) this.is_loading_searching_user = false;
            }
        },
        editSearchedUser(_user) {
            // 수정 중 뒤로가기 등으로 목록이 바뀌어도 비교·갱신 기준이 흔들리지 않게 원본을 따로 둔다
            this.editOriginal = {..._user};
            this.editUserFormValue = {..._user};
            this.$nextTick(() => {
                this.$bvModal.show('user-edit-modal');
            });
        },
        resetSelectedUser() {
            this.$refs['user-table'].clearSelected();
            this.searchResults.forEach(elem => {
                elem.select = false;
            });
            this.selectedUserNum = 0;
            this.selectedUserIds = {};
        },
        async saveEditedUser() {
            const {id, user_name, e_mail, company_id, is_admin} = this.editUserFormValue;
            const trimmedName = (user_name || '').trim();
            const trimmedMail = (e_mail || '').trim();
            const original = this.editOriginal;
            if (!trimmedName && !(original && !original.user_name)) {
                this.$EventBus.$emit('toast-failure', this.$t('유저 수정 및 저장'), '사용자 이름을 입력해주세요.');
                return;
            }
            if (!(original && trimmedMail === original.e_mail) && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedMail)) {
                this.$EventBus.$emit('toast-failure', this.$t('유저 수정 및 저장'), '이메일 형식이 올바르지 않습니다.');
                return;
            }
            if (!/^\d+$/.test(String(company_id ?? '').trim())) {
                this.$EventBus.$emit('toast-failure', this.$t('유저 수정 및 저장'), '기업 ID 는 숫자로 입력해주세요.');
                return;
            }

            // 레벨/소속/이메일 변경은 권한에 직접 영향을 주므로 전후 값을 보여주고 확인받는다
            const before = this.editOriginal;
            let companyName = null;
            if (before) {
                const changes = [];
                if (Number(before.is_admin) !== Number(is_admin)) {
                    changes.push(`레벨: ${getUserLevelLabel(Number(before.is_admin))} → ${getUserLevelLabel(Number(is_admin))}`);
                }
                if (String(before.company_id) !== String(company_id).trim()) {
                    try {
                        const info = await this.$axios.get(`/admin/company/${String(company_id).trim()}/info`);
                        companyName = info.data.company.company_name;
                    } catch (error) {
                        this.$EventBus.$emit('toast-failure', this.$t('유저 수정 및 저장'),
                            error.response && error.response.status === 404
                                ? `기업 ID ${company_id} 에 해당하는 기업이 없습니다.`
                                : getErrorMessage(error, '기업 정보를 확인하지 못했습니다.'));
                        return;
                    }
                    changes.push(`소속 기업: ${before.company_name || '-'} (ID ${before.company_id}) → ${companyName} (ID ${String(company_id).trim()})`);
                }
                if ((before.e_mail || '') !== trimmedMail) {
                    changes.push(`이메일: ${before.e_mail} → ${trimmedMail}`);
                }
                if (changes.length > 0) {
                    const h = this.$createElement;
                    const confirmed = await this.$showModal({
                        type: 'confirm',
                        state: 'warning',
                        title: '사용자 정보 변경',
                        message: h('div', [
                            h('p', {class: 'mb-2'}, `${before.user_name} 님의 다음 항목을 변경합니다. 소속을 바꾸면 기존 소속의 권한을 잃을 수 있습니다.`),
                            h('ul', {class: 'mb-0 pl-3'}, changes.map(change => h('li', change))),
                        ]),
                    });
                    if (!confirmed) {
                        return;
                    }
                }
            }

            try {
                this.is_saving_editing_user = true;

                const response = await this.$axios.post('/users/admin', {
                    id, user_name: trimmedName, e_mail: trimmedMail, company_id, is_admin
                });

                // 목록의 같은 사용자 행(id 기준)만 갱신한다. 목록이 바뀌어 없으면 건너뛴다
                const rowIndex = (this.searchResults || []).findIndex(user => user.id === id);
                if (rowIndex > -1) {
                    let _updating_user = {...this.searchResults[rowIndex]};

                    _updating_user.id = response.data.id,
                    _updating_user.user_name = response.data.user_name,
                    _updating_user.e_mail = response.data.e_mail,
                    _updating_user.company_id = response.data.company_id,
                    _updating_user.is_admin = response.data.is_admin;
                    // 응답에 기업명이 없어, 소속을 바꿨으면 확인 단계에서 조회한 새 기업명을 쓴다
                    if (companyName != null) _updating_user.company_name = companyName;

                    this.$set(this.searchResults, rowIndex, _updating_user);
                }
                this.closeEditModal();
                this.$EventBus.$emit('toast-success', this.$t('유저 수정 및 저장'), '사용자 정보를 저장했습니다.');
            } catch (error) {
                if (error?.response?.status === 401) return;

                this.$EventBus.$emit('toast-failure', this.$t('유저 수정 및 저장'), getErrorMessage(error, '유저 데이터 저장 도중 오류가 발생했습니다. 관련 유저 검색을 재검색해 편집 및 저장해주세요.'));
            } finally {
                this.is_saving_editing_user = false;
            }
        },
        resetUserEditUserForm() {
            if (this.editUserFormValue.index === -1) {
                this.$EventBus.$emit('toast-failure', this.$t('유저 수정 및 저장'), '초기화할 유저 데이터가 없습니다. 유저 검색 결과 테이블에서 다시 시도해주세요');
                this.$bvModal.show('user-edit-modal');

                this.$nextTick(() => {
                    this.editUserFormValue = {
                        index: null, user_name: null, e_mail: null, company_id: null, is_admin: null
                    };
                });
            } else {
                const _editing_user = this.searchResults[this.editUserFormValue.index];
                if (!_editing_user) {
                    this.$EventBus.$emit('toast-failure', this.$t('유저 수정 및 저장'), '초기화할 유저 데이터가 없습니다. 유저 검색 결과 테이블에서 다시 시도해주세요');
                    this.$bvModal.show('user-edit-modal');

                    this.$nextTick(() => {
                        this.editUserFormValue = {
                            index: null, user_name: null, e_mail: null, company_id: null, is_admin: null
                        };
                    });
                } else {
                    this.editUserFormValue = {..._editing_user};
                }
            }
        },

        closeEditModal() {
            this.editConfirmedClose = true;
            this.$bvModal.hide('user-edit-modal');
        },
        resetEditForm() {
            this.editConfirmedClose = false;
            this.editOriginal = null;
            this.editUserFormValue = {index: null, user_name: null, e_mail: null, company_id: null, is_admin: null};
        },
        // ESC·바깥 클릭·X 로 닫을 때 바꾼 값이 있으면 확인한다 (취소 버튼·저장 완료는 closeEditModal 로 바로 닫음)
        onEditModalHide(bvEvent) {
            if (this.editConfirmedClose || !this.isEditFormDirty()) return;
            bvEvent.preventDefault();
            this.$showModal({
                type: 'confirm',
                state: 'warning',
                title: '저장하지 않은 편집',
                message: '저장하지 않은 편집 내용이 있습니다. 창을 닫으면 편집 내용이 사라집니다. 닫을까요?',
            }).then((ok) => {
                if (ok) this.closeEditModal();
            });
        },
        isEditFormDirty() {
            const original = this.editOriginal;
            if (!original) return false;
            return ['user_name', 'e_mail', 'company_id', 'is_admin']
                .some(key => String(this.editUserFormValue[key] ?? '').trim() !== String(original[key] ?? '').trim());
        },

        async giveProjectToAuditor() {
            if (this.is_giving_project) return;
            const title = this.$t('실사위원 프로젝트 접근 권한 부여');
            const selectedUserIdsArray = Object.keys(this.selectedUserIds);
            const projectCode = this.project_code;
            if (!projectCode) {
                this.$EventBus.$emit('toast-failure', title, '프로젝트 코드를 입력해주세요.');
                return;
            }
            if (this.selectedUserNum === 0) {
                this.$EventBus.$emit('toast-failure', title, '권한부여를 위해 사용자를 선택해주세요.');
                return;
            }
            const selectedUsers = selectedUserIdsArray.map(id => this.mapSearchUser[id] || this.selectedUserIds[id]);
            if (selectedUsers.some(user => user.is_admin !== 2 && user.is_admin !== 3)) {
                this.$EventBus.$emit('toast-failure', title, '권한부여를 위해 lv.2 또는 lv.3 사용자를 선택해주세요.');
                return;
            }
            const confirmed = await this.$showModal({
                type: 'confirm',
                state: 'warning',
                title: '프로젝트 접근 권한 부여',
                message: `선택한 ${selectedUsers.length}명(${selectedUsers.map(user => user.user_name).join(', ')})에게 프로젝트 ${projectCode} 접근 권한을 부여합니다.`,
            });
            if (!confirmed) return;

            try {
                this.is_giving_project = true;
                const response = await this.$axios.post('/users/auditors/projects/' + projectCode, selectedUserIdsArray);
                if (response.data.updated || response.data.updated.length) {
                    response.data.updated.forEach(_user => {
                        const _updating_user_index = this.searchResults.findIndex(({id}) => id === _user.user_id);
                        if (_updating_user_index > -1) {
                            const _updating_user = {...this.searchResults[_updating_user_index]};
                            // 이미 가진 코드는 다시 넣지 않는다 (칩 중복 방지)
                            const codes = _updating_user.project_codes || [];
                            _updating_user.project_codes = codes.includes(projectCode) ? codes : [...codes, projectCode];
                            this.$set(this.searchResults, _updating_user_index, _updating_user);
                        }
                    });

                    this.$EventBus.$emit('toast-success', title, `실사위원의 ${projectCode} 프로젝트 접근 권한 부여를 완료했습니다.`);
                }
            } catch (error) {
                if (error?.response?.status === 401) return;
                // 오류 본문은 JSON {message} 또는 평문 — 객체를 그대로 그리면 빈칸이 된다 (df_backend_custom#2175).
                const reason = getErrorMessage(error, '');
                const h = this.$createElement;
                this.$EventBus.$emit('toast-failure', title, h('div', {}, [
                    h('div', {}, `실사위원의 ${projectCode} 프로젝트 접근 권한 부여 중 오류가 발생했습니다.`),
                    reason ? h('div', {}, reason) : null,
                ]));
            } finally {
                this.is_giving_project = false;
            }
        },
        async deleteAditorProjectAccess(user, _project_code) {
            const confirmed = await this.$showModal({
                type: 'confirm',
                state: 'warning',
                title: '프로젝트 접근 권한 해제',
                message: `${user.user_name} 님의 ${_project_code} 프로젝트 접근 권한을 해제할까요? 해제하면 이 실사위원은 해당 프로젝트에 접근할 수 없습니다.`,
            });
            if (!confirmed) {
                return;
            }
            try {
                const _res_discarded = await this.$axios.delete(`/users/auditor/${user.id}`, {
                    params: {project_code: _project_code}
                });

                const _updated_user = _res_discarded.data;
                if (!_updated_user[0].project_codes) {
                    this.$set(this.searchResults, user.index, {...user, project_codes: null});
                } else {
                    const _updating_project_codes = _updated_user[0].project_codes.split(',');
                    this.$set(this.searchResults, user.index, {...user, project_codes: _updating_project_codes});
                }

                this.$EventBus.$emit('toast-success', this.$t('실사위원 프로젝트 접근 권한 해제'), `${user.user_name} - 실사위원의 ${_project_code} 프로젝트 접근 권한 해제를 완료했습니다.`);
            } catch (error) {
                this.$EventBus.$emit('toast-failure', this.$t('실사위원 프로젝트 접근 권한 해제'), `${user.user_name} - 실사위원 ${_project_code} 프로젝트 접근 권한 해제 중 장애가 발생했습니다. 관련 유저 검색을 재검색해 편집 및 저장해주세요.`);
            }
        },
        handleRowSelection(row) {
            // 선택된 항목의 user_id를 selectedUserIds 배열에 추가 또는 제거합니다.
            if (row.select) {
                this.$set(this.selectedUserIds, row.id, row);

            } else {
                this.$delete(this.selectedUserIds, row.id);
            }

            // 선택된 사용자 수를 업데이트합니다.
            this.selectedUserNum = Object.keys(this.selectedUserIds).length;
        }
    }
};
</script>

<style lang="scss">
/* 줄어드는 4칸이 각각 ~160 아래로 좁아지면 가로 스크롤 — 고정 열 합 346(선택 48 · No. 56 · 레벨 150 · 행 액션 92) + 160x4 */
.admin-user-table {
    min-width: 1000px;
}
</style>
